import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';

const Settings: React.FC = () => {
  const location = useLocation();
  const { user, updateProfile, checkHandleAvailability } = useAuth();
  const { addToast } = useToast();

  // Scroll to top when navigating to this route
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  const [isSaving, setIsSaving] = useState(false);
  const [handleStatus, setHandleStatus] = useState<'idle' | 'checking' | 'available' | 'taken' | 'invalid'>('idle');
  const [handleSuggestions, setHandleSuggestions] = useState<string[]>([]);
  
  // Form state
  const [formData, setFormData] = useState({
    handle: ''
  });
  
  // Handle checking with debounce
  const handleCheckTimeout = useRef<NodeJS.Timeout | null>(null);
  
  const checkHandle = useCallback(async (handle: string, currentHandle?: string) => {
    // Don't check if it's the current user's existing handle
    if (handle === currentHandle) {
      setHandleStatus('available');
      setHandleSuggestions([]);
      return;
    }
    
    if (!handle || handle.trim().length < 3) {
      setHandleStatus('idle');
      setHandleSuggestions([]);
      return;
    }
    
    setHandleStatus('checking');
    
    try {
      const result = await checkHandleAvailability(handle);
      if (result.available) {
        setHandleStatus('available');
        setHandleSuggestions([]);
      } else {
        setHandleStatus('taken');
        setHandleSuggestions(result.suggestions || []);
      }
    } catch (error) {
      setHandleStatus('invalid');
      setHandleSuggestions([]);
    }
  }, [checkHandleAvailability]);

  const handleHandleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, handle: value }));
    
    // Clear previous timeout
    if (handleCheckTimeout.current) {
      clearTimeout(handleCheckTimeout.current);
    }
    
    // Debounce the handle check
    handleCheckTimeout.current = setTimeout(() => {
      checkHandle(value, user?.handle);
    }, 500);
  };

  // Initialize form with user's current handle
  useEffect(() => {
    if (user) {
      setFormData({
        handle: user.handle || ''
      });
      // Reset handle status when loading user data
      setHandleStatus('idle');
      setHandleSuggestions([]);
    }
  }, [user]);

  const handleSaveHandle = async () => {
    if (!user || !updateProfile) return;
    
    // Validate handle before saving
    if (!formData.handle || !formData.handle.trim()) {
      addToast('Handle cannot be empty.', 'error');
      return;
    }
    
    if (formData.handle.trim().length < 3) {
      addToast('Handle must be at least 3 characters long.', 'error');
      return;
    }
    
    if (handleStatus === 'taken') {
      addToast('Please choose an available handle before saving.', 'error');
      return;
    }
    
    // Don't save if handle hasn't changed
    if (formData.handle.trim() === user.handle) {
      addToast('Handle is already set to this value.', 'info');
      return;
    }
    
    setIsSaving(true);
    
    try {
      const profileData = {
        handle: formData.handle.trim()
      };

      const success = await updateProfile(profileData);
      if (success) {
        addToast('Handle updated successfully!', 'success');
        // Reset handle status since we now have a new valid handle
        setHandleStatus('idle');
        setHandleSuggestions([]);
      } else {
        addToast('Failed to update handle. Please try again.', 'error');
      }
    } catch (error) {
      console.error('Error updating handle:', error);
      addToast('An error occurred while updating your handle.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin h-8 w-8 border-2 border-custom-blue-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-start min-h-screen py-8">
      <main className="flex flex-col items-center justify-center w-full flex-1 px-4 md:px-20">
        <div className="w-full max-w-2xl">          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">Settings</h1>
            <p className="text-gray-300">Manage your account settings and preferences.</p>
          </div>

          {/* User Info Card */}
          <Card className="mb-6 bg-paper border-parchment-300 shadow-paper text-custom-blue-900">
            <CardHeader>
              <CardTitle className="text-custom-blue-900">Account Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center space-x-4">
                <Avatar className="w-16 h-16">
                  <AvatarImage src={user.picture} alt="Profile Picture" />
                  <AvatarFallback className="text-lg bg-parchment-100 text-custom-blue-900">
                    {user.name.substring(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="text-lg font-semibold text-custom-blue-900">{user.name}</h3>
                  <p className="text-custom-blue-600">{user.email}</p>
                  <p className="text-custom-blue-600">@{user.handle}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Handle Settings Card */}
          <Card className="bg-paper border-parchment-300 shadow-paper text-custom-blue-900">
            <CardHeader>
              <CardTitle className="text-custom-blue-900">Handle Settings</CardTitle>
              <p className="text-sm text-custom-blue-600">
                Your handle is used in your profile URL (/@yourhandle) and to identify you on the platform.
                Choose carefully as this affects how others find and mention you.
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-2 text-custom-blue-900">
                  Handle <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-custom-blue-600 text-sm">@</span>
                  </div>
                  <input
                    type="text"
                    maxLength={30}
                    value={formData.handle}
                    onChange={handleHandleChange}
                    className="w-full pl-8 pr-3 py-2 border border-parchment-300 rounded-md bg-paper text-custom-blue-900 placeholder-custom-blue-500 focus:outline-none focus:ring-2 focus:ring-custom-blue-500"
                    placeholder="yourhandle"
                  />
                  {handleStatus === 'checking' && (
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                      <div className="animate-spin h-4 w-4 border-2 border-custom-blue-600 border-t-transparent rounded-full"></div>
                    </div>
                  )}
                </div>
                <p className="text-xs text-custom-blue-600 mt-1">
                  Your unique handle (3-30 characters, letters, numbers, and underscores only)
                </p>
                
                {/* Handle Status Messages */}
                {handleStatus === 'available' && formData.handle !== user.handle && (
                  <p className="text-sm text-green-600 mt-1">
                    ✓ Handle is available!
                  </p>
                )}
                {handleStatus === 'taken' && (
                  <div className="mt-1">
                    <p className="text-sm text-red-600">
                      ✗ Handle is already taken
                    </p>
                    {handleSuggestions.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs text-custom-blue-600">Suggestions:</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {handleSuggestions.map((suggestion) => (
                            <button
                              key={suggestion}
                              type="button"
                              onClick={() => {
                                setFormData(prev => ({ ...prev, handle: suggestion }));
                                checkHandle(suggestion, user?.handle);
                              }}
                              className="px-2 py-1 text-xs bg-custom-blue-100 text-custom-blue-700 rounded hover:bg-custom-blue-200 transition-colors"
                            >
                              @{suggestion}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {handleStatus === 'invalid' && (
                  <p className="text-sm text-red-600 mt-1">
                    ✗ Invalid handle format
                  </p>
                )}
                {formData.handle === user.handle && handleStatus !== 'checking' && (
                  <p className="text-sm text-custom-blue-600 mt-1">
                    This is your current handle
                  </p>
                )}
              </div>

              {/* Current Profile URL Preview */}
              <div className="bg-parchment-50 p-4 rounded-md border border-parchment-200">
                <label className="block text-sm font-medium mb-1 text-custom-blue-900">
                  Profile URL Preview
                </label>
                <code className="text-sm text-custom-blue-700 bg-parchment-100 px-2 py-1 rounded">
                  {window.location.origin}/@{formData.handle || 'yourhandle'}
                </code>
              </div>

              {/* Warning Message */}
              <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-amber-800">
                      Important Notice
                    </h3>
                    <div className="mt-2 text-sm text-amber-700">
                      <p>
                        Changing your handle will update your profile URL and how others mention you. 
                        The old handle will become available for others to use. Make sure to update any 
                        bookmarks or links to your profile.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Save Button */}
              <div className="flex justify-end">
                <Button 
                  onClick={handleSaveHandle}
                  disabled={isSaving || handleStatus === 'taken' || handleStatus === 'invalid' || formData.handle === user.handle || !formData.handle.trim()}
                  className="bg-custom-blue-600 hover:bg-custom-blue-700 text-white"
                >
                  {isSaving ? 'Updating...' : 'Update Handle'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Future Settings Sections */}
          <Card className="mt-6 bg-paper border-parchment-300 shadow-paper text-custom-blue-900">
            <CardHeader>
              <CardTitle className="text-custom-blue-900">Other Settings</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8 text-custom-blue-600">
                <p>Additional settings coming soon...</p>
                <p className="text-sm mt-2">Privacy settings, notifications, and more will be available here.</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default Settings;
